import React, { useEffect, useState, useContext } from 'react'
import * as XLSX from 'xlsx'
import Highcharts from 'highcharts'
import moment from 'moment'

import LoadingContext from '../../context/Loading/Loading'

import { formatNumber } from '../../utilities/helpers'

import {
  getOrgEventsForReport,
  getROEventSalesReport,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import Stack from 'react-bootstrap/Stack'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'

import { ReportActions } from '../ReportActions'
import { EventSelector } from '../EventSelector'
import Stats from './Stats'
import PerformanceSales from './PerformanceSales'
import SalesTable from './SalesTable'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { LoadingContainer } from '../LoadingContainer'
import { InfoIconOutline } from '../InfoIconOutline'
import { EmptyContainer } from '../EmptyContainer'

import './analyticsWrapper.scss'

// define Highcharts color theme
Highcharts.setOptions({
  colors: ['#3E8BF7', '#FFD166', '#EF466F', '#9757D7', '#45B26B'],
})

export default function AnalyticsWrapper({ organization }) {
  const { showLoading, hideLoading, isLoading } = useContext(LoadingContext)

  const [events, setEvents] = useState()

  const [selectedEvent, setSelectedEvent] = useState()

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)

  const [jsonData, setJsonData] = useState([])

  const [salesData, setSalesData] = useState([])

  const [totalInventory, setTotalInventory] = useState(0)

  const [compChartData, setCompChartData] = useState()

  useEffect(() => {
    showLoading()
    loadEvents(organization.uuid)
  }, [])

  useEffect(() => {
    if (selectedEvent) loadSalesData()
  }, [selectedEvent, refreshToken])

  const loadEvents = async (organizationUUID) => {
    let res = await getOrgEventsForReport(organizationUUID)

    if (res.status === 200) {
      setEvents(res.data)
      hideLoading()
    }
  }

  const loadSalesData = async () => {
    setIsLoadingData(true)
    getROEventSalesReport(selectedEvent)
      .then((res) => {
        setJsonData(res.data)
        setSalesData(res.data?.sales_data)
        setTotalInventory(res.data?.event?.venue[0]?.capacity)
        let comps = res.data.sales_data.filter((d) => d.complimentary === true)
        let compLabels = comps.map((d) => (d.comp_label ? d.comp_label : 'N/A'))
        compLabels = new Set(compLabels)
        compLabels = [...compLabels].sort()

        let data = compLabels.map((label) => {
          return [
            label,
            comps
              .filter((c) =>
                label === 'N/A' ? c.comp_label === '' : c.comp_label === label
              )
              .reduce((total, num) => total + num.ticketCounts, 0),
          ]
        })
        setCompChartData(data)
      })
      .then(() => {
        setIsLoadingData(false)
        completeRefresh()
      })
      .catch((err) => {
        console.error(err)
        setIsLoadingData(false)
        completeRefresh()
      })
  }

  const salesWorkBook = (data) => {
    let arr = []
    let online = data.filter((d) => d.source === 'website')
    let boxOfficeCash = data.filter(
      (d) =>
        d.source === 'box_office' &&
        d.complimentary === false &&
        d.payment_method_type === 'cash'
    )
    let boxOfficeCard = data.filter(
      (d) =>
        d.source === 'box_office' &&
        !d.payment_method_type &&
        d.complimentary === false
    )
    let boxOfficeComplementary = data.filter(
      (d) => d.source === 'box_office' && d.complimentary === true
    )
    let indGroupCash = data.filter(
      (d) =>
        d.source === 'ticket_assignment' &&
        d.assignment_type === 'individual' &&
        d.payment_option === 'cash'
    )
    let indGroupCard = data.filter(
      (d) =>
        d.source === 'ticket_assignment' &&
        d.assignment_type === 'individual' &&
        d.payment_option === 'card'
    )
    let groupGroupCash = data.filter(
      (d) => d.assignment_type === 'group' && d.payment_option === 'cash'
    )
    let groupGroupCard = data.filter(
      (d) => d.assignment_type === 'group' && d.payment_option === 'card'
    )
    let groupComplementary = data.filter((d) => d.assignment_type === 'comp')
    let packagesSold = data.filter((d) => d.salesChannel === 'packages')
    arr.push(
      {
        'Sales Channel': 'Online - (Blocktickets.xyz)',
        qty: online.reduce((total, num) => total + num.ticketCounts, 0),
        'Gross Sales': online.reduce((total, num) => total + num.total, 0),
        'Net Sales': online.reduce(
          (total, num) =>
            total +
            (num.total -
              num.serviceFeeTotal -
              num.taxAmount -
              num.stripeFee -
              num.chargebackFee),
          0
        ),
      },
      {
        'Sales Channel': 'Box Office – Card',
        qty: boxOfficeCard.reduce((total, num) => total + num.ticketCounts, 0),
        'Gross Sales': boxOfficeCard.reduce(
          (total, num) => total + num.total,
          0
        ),
        'Net Sales': boxOfficeCard.reduce(
          (total, num) =>
            total +
            (num.total -
              num.serviceFeeTotal -
              num.taxAmount -
              num.stripeFee -
              num.chargebackFee),
          0
        ),
      },
      {
        'Sales Channel': 'Box Office – Cash/Check',
        qty: boxOfficeCash.reduce((total, num) => total + num.ticketCounts, 0),
        'Gross Sales': boxOfficeCash.reduce(
          (total, num) => total + num.total,
          0
        ),
        'Net Sales': boxOfficeCash.reduce(
          (total, num) =>
            total + (num.total - num.serviceFeeTotal - num.taxAmount),
          0
        ),
      },
      {
        'Sales Channel': 'Box Office – Complimentary',
        qty: boxOfficeComplementary.reduce(
          (total, num) => total + num.ticketCounts,
          0
        ),
        'Gross Sales': 0,
        'Net Sales': 0,
      },
      {
        'Sales Channel': 'Assigned – Individual (<20 Tickets) – Card',
        qty: indGroupCard.reduce((total, num) => total + num.ticketCounts, 0),
        'Gross Sales': indGroupCard.reduce(
          (total, num) => total + num.total,
          0
        ),
        'Net Sales': indGroupCard.reduce(
          (total, num) =>
            total +
            (num.total -
              num.serviceFeeTotal -
              num.taxAmount -
              num.stripeFee -
              num.chargebackFee),
          0
        ),
      },
      {
        'Sales Channel': 'Assigned – Individual (<20 Tickets) – Cash/Check',
        qty: indGroupCash.reduce((total, num) => total + num.ticketCounts, 0),
        'Gross Sales': indGroupCash.reduce(
          (total, num) => total + num.total,
          0
        ),
        'Net Sales': indGroupCash.reduce(
          (total, num) =>
            total + (num.total - num.serviceFeeTotal - num.taxAmount),
          0
        ),
      },
      {
        'Sales Channel': 'Assigned – Group (20+ Tickets) – Card',
        qty: groupGroupCard.reduce((total, num) => total + num.ticketCounts, 0),
        'Gross Sales': groupGroupCard.reduce(
          (total, num) => total + num.total,
          0
        ),
        'Net Sales': groupGroupCard.reduce(
          (total, num) =>
            total +
            (num.total -
              num.serviceFeeTotal -
              num.taxAmount -
              num.stripeFee -
              num.chargebackFee),
          0
        ),
      },
      {
        'Sales Channel': 'Assigned – Group (20+ Tickets) – Cash/Check',
        qty: groupGroupCash.reduce((total, num) => total + num.ticketCounts, 0),
        'Gross Sales': groupGroupCash.reduce(
          (total, num) => total + num.total,
          0
        ),
        'Net Sales': groupGroupCash.reduce(
          (total, num) =>
            total + (num.total - num.serviceFeeTotal - num.taxAmount),
          0
        ),
      },
      {
        'Sales Channel': 'Assigned – Complimentary',
        qty: groupComplementary.reduce(
          (total, num) => total + num.ticketCounts,
          0
        ),
        'Gross Sales': 0,
        'Net Sales': 0,
      },
      {
        'Sales Channel': 'Packages',
        qty: packagesSold.reduce((total, num) => total + num.ticketCounts, 0),
        'Gross Sales': packagesSold
          .reduce((total, num) => total + num.total, 0)
          .toFixed(2),
        'Net Sales': 0,
      }
    )
    return arr
  }

  const downloadReport = async () => {
    const worksheet = XLSX.utils.json_to_sheet(jsonData.sales_data)
    const worksheet2 = XLSX.utils.json_to_sheet(
      salesWorkBook(jsonData.sales_data)
    )
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet2, 'Sales Channel')
    XLSX.utils.book_append_sheet(workbook, worksheet, 'All Data')
    let name = jsonData?.event?.name
    let eventName = name.split('vs.')[1]
    let finalName

    if (eventName) {
      finalName = eventName.trim()
    } else {
      finalName = name.trim()
    }

    XLSX.writeFile(
      workbook,
      `${moment(events.find((e) => e.uuid === selectedEvent)?.start).format(
        'MMM D, YYYY'
      )} vs ${finalName}-.xlsx`
    )
  }

  const ticketsAssigned = jsonData?.totalQuantity

  const ticketsScanned = jsonData?.totalScanned

  return (
    <>
      {isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : (
        <section className="spacer-sm" id="analytics-wrapper">
          <header className="section-header-sm">
            <div className="section-heading">
              <h1>Analytics</h1>
            </div>
            <p className="section-header-desc">
              This report shows ticket sales, assignment types, scans, and
              channel performance by day.
            </p>
          </header>
          {events?.length > 0 ? (
            <>
              <ReportActions
                isLoading={isLoadingData}
                onRefresh={refresh}
                isRefreshing={isLoadingData}
                selected={selectedEvent}
              >
                <EventSelector
                  selected={selectedEvent}
                  setSelected={setSelectedEvent}
                  events={events}
                  isLoading={isLoadingData}
                />
              </ReportActions>
              <Results
                selectedEvent={selectedEvent}
                isLoading={isLoadingData}
                data={jsonData}
                salesData={salesData}
                ticketsAssigned={ticketsAssigned}
                totalInventory={totalInventory}
                ticketsScanned={ticketsScanned}
                loadSalesData={loadSalesData}
                compChartData={compChartData}
              />
            </>
          ) : (
            <Card body>
              <EmptyContainer style="center lg">
                <p>No events created</p>
                <p className="subtitle">
                  Create an event to start tracking its analytics.
                </p>
              </EmptyContainer>
            </Card>
          )}
        </section>
      )}
    </>
  )
}

function Results({
  isLoading,
  selectedEvent,
  data,
  salesData,
  totalInventory,
  ticketsAssigned,
  ticketsScanned,
  loadSalesData,
  compChartData,
}) {
  const [key, setKey] = useState('insights')

  if (!selectedEvent) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>Select an event to view its analytics</p>
        </EmptyContainer>
      </Card>
    )
  }
  if (isLoading) {
    return <LoadingContainer />
  }
  if (salesData?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg">
          <p>No data</p>
          <p className="subtitle">
            There's currently no data for this event. Please choose another
            event.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Tabs
      defaultActiveKey="insights"
      variant="pills"
      activeKey={key}
      onSelect={(k) => setKey(k)}
      className="mb-4 w-max-content nav-pills-default"
    >
      <Tab eventKey="insights" title="Event Insights">
        <Stack gap={4}>
          <Row>
            <Col>
              <Card
                body
                className="flex-grow-1 card--xs fw-normal card-with-border"
              >
                <div className="card-body-heading--flex-space-between">
                  <div className="flex gap-2">
                    <Card.Title
                      className="card-title-xs card-title-thinner info-text info-text-md info-text-capacity"
                      as="h5"
                    >
                      Capacity
                    </Card.Title>
                    <span className="small small-bold">
                      {formatNumber(totalInventory)}
                    </span>{' '}
                  </div>
                  <InfoIconOutline />
                </div>
              </Card>
            </Col>
            <Col>
              <Card
                body
                className="flex-grow-1 card--xs fw-normal card-with-border"
              >
                <div className="card-body-heading--flex-space-between">
                  <div className="flex gap-2">
                    <Card.Title
                      className="card-title-xs card-title-thinner info-text info-text-md info-text-quantity"
                      as="h5"
                    >
                      Total Quantity
                    </Card.Title>
                    <span className="small small-bold">
                      {formatNumber(ticketsAssigned)}
                    </span>{' '}
                  </div>
                  <InfoIconOutline />
                </div>
              </Card>
            </Col>
            <Col>
              <Card
                body
                className="flex-grow-1 card--xs fw-normal card-with-border"
              >
                <div className="card-body-heading--flex-space-between">
                  <div className="flex gap-2">
                    <Card.Title
                      className="card-title-xs card-title-thinner info-text info-text-md info-text-scanned"
                      as="h5"
                    >
                      Tickets Scanned
                    </Card.Title>
                    <span className="small small-bold">
                      {formatNumber(ticketsScanned)}
                    </span>{' '}
                  </div>
                  <InfoIconOutline />
                </div>
              </Card>
            </Col>
          </Row>
          <Stats data={salesData} jsonData={data} />
          <PerformanceSales data={salesData} compChartData={compChartData} />
        </Stack>
      </Tab>
      {/* <Tab eventKey="sales" title="Sales Breakdown">
        <div className="col-12 mt-4">
          <SalesTable
            data={salesData}
            refreshReport={loadSalesData}
            jsonData={data}
          />
        </div>
      </Tab> */}
    </Tabs>
  )
}
